import { User } from '@/types'

/**
 * Authentication utility functions
 * Provides helper functions for common authentication operations
 * These functions mirror the backend User model permission methods
 */

/**
 * Check if user has a specific role
 */
export const hasRole = (user: User | null, role: string): boolean => {
  return user?.role === role
}

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (user: User | null, roles: string[]): boolean => {
  return user ? roles.includes(user.role) : false
}

/**
 * Check if user has all of the specified roles
 */
export const hasAllRoles = (user: User | null, roles: string[]): boolean => {
  return user ? roles.every(role => user.role === role) : false
}

// ============================================================================
// ROLE-BASED PROPERTIES (mirroring backend User model properties)
// ============================================================================

/**
 * Check if user has platform admin role
 */
export const isAdmin = (user: User | null): boolean => {
  return hasRole(user, 'admin')
}

/**
 * Check if user has merchant admin role
 */
export const isMerchantAdmin = (user: User | null): boolean => {
  return hasRole(user, 'merchant_admin')
}

/**
 * Check if user has merchant user role
 */
export const isMerchantUser = (user: User | null): boolean => {
  return hasRole(user, 'merchant_user')
}

/**
 * Check if user has submerchant admin role
 */
export const isSubmerchantAdmin = (user: User | null): boolean => {
  return hasRole(user, 'submerchant_admin')
}

/**
 * Check if user has submerchant user role
 */
export const isSubmerchantUser = (user: User | null): boolean => {
  return hasRole(user, 'submerchant_user')
}

/**
 * Check if user has auditor role
 */
export const isAuditor = (user: User | null): boolean => {
  return hasRole(user, 'auditor')
}

/**
 * Check if user is a merchant (admin or user)
 */
export const isMerchant = (user: User | null): boolean => {
  return user ? ['merchant_admin', 'merchant_user'].includes(user.role) : false
}

/**
 * Check if user is merchant-related (admin or user)
 */
export const isMerchantRelated = (user: User | null): boolean => {
  return user ? ['merchant_admin', 'merchant_user'].includes(user.role) : false
}

/**
 * Check if user is submerchant-related (admin or user)
 */
export const isSubmerchantRelated = (user: User | null): boolean => {
  return user ? ['submerchant_admin', 'submerchant_user'].includes(user.role) : false
}

/**
 * Check if user is staff (admin or superuser)
 */
export const isStaff = (user: User | null): boolean => {
  return user ? user.is_staff || user.is_superuser : false
}

/**
 * Check if user is superuser
 */
export const isSuperUser = (user: User | null): boolean => {
  return user ? user.is_superuser : false
}

// ============================================================================
// PERMISSION METHODS (mirroring backend User model methods)
// ============================================================================

/**
 * Check if user can manage merchants (platform admin only)
 */
export const canManageMerchants = (user: User | null): boolean => {
  return user ? user.role === 'admin' || user.is_superuser : false
}

/**
 * Check if user can manage their own merchant
 */
export const canManageOwnMerchant = (user: User | null): boolean => {
  return user ? user.role === 'merchant_admin' || user.role === 'admin' || user.is_superuser : false
}

/**
 * Check if user can manage submerchants
 */
export const canManageSubmerchants = (user: User | null): boolean => {
  return user ? user.role === 'merchant_admin' || user.role === 'admin' || user.is_superuser : false
}

/**
 * Check if user can manage their own submerchant
 */
export const canManageOwnSubmerchant = (user: User | null): boolean => {
  return user ? user.role === 'submerchant_admin' || user.role === 'merchant_admin' || user.role === 'admin' || user.is_superuser : false
}

/**
 * Check if user can access admin panel
 */
export const canAccessAdminPanel = (user: User | null): boolean => {
  return user ? user.role === 'admin' || user.is_superuser || user.is_staff : false
}

/**
 * Check if user can access merchant dashboard
 */
export const canAccessMerchantDashboard = (user: User | null): boolean => {
  return user ? user.role === 'merchant_admin' || user.role === 'submerchant_admin' || user.role === 'admin' || user.is_superuser : false
}

/**
 * Check if user can view all transactions
 */
export const canViewAllTransactions = (user: User | null): boolean => {
  return user ? user.role === 'admin' || user.is_superuser : false
}

/**
 * Check if user can view their merchant's transactions
 */
export const canViewMerchantTransactions = (user: User | null): boolean => {
  return user ? user.role === 'merchant_admin' || user.role === 'submerchant_admin' : false
}

/**
 * Check if user can manage system settings
 */
export const canManageSystemSettings = (user: User | null): boolean => {
  return user ? user.is_superuser : false
}

/**
 * Check if user can audit transactions
 */
export const canAuditTransactions = (user: User | null): boolean => {
  return user ? user.role === 'auditor' || user.role === 'admin' || user.is_superuser : false
}

/**
 * Check if user can view audit logs
 */
export const canViewAuditLogs = (user: User | null): boolean => {
  return user ? user.role === 'auditor' || user.role === 'admin' || user.is_superuser : false
}

/**
 * Check if user can monitor system health
 */
export const canMonitorSystemHealth = (user: User | null): boolean => {
  return user ? user.role === 'auditor' || user.role === 'admin' || user.is_superuser : false
}

/**
 * Check if user can generate reports
 */
export const canGenerateReports = (user: User | null): boolean => {
  return user ? user.role === 'auditor' || user.role === 'admin' || user.is_superuser : false
}

/**
 * Check if user can create submerchants
 */
export const canCreateSubmerchants = (user: User | null): boolean => {
  return user ? user.role === 'merchant_admin' || user.role === 'admin'  || user.is_superuser : false
}

/**
 * Check if user can manage other users
 */
export const canManageUsers = (user: User | null): boolean => {
  return user ? user.role === 'admin' || user.is_superuser : false
}

/**
 * Check if user can manage users within their merchant
 */
export const canManageMerchantUsers = (user: User | null): boolean => {
  return user ? user.role === 'merchant_admin' || user.role === 'admin' || user.is_superuser : false
}

/**
 * Check if user can manage users within their submerchant
 */
export const canManageSubmerchantUsers = (user: User | null): boolean => {
  return user ? user.role === 'submerchant_admin' || user.role === 'merchant_admin' || user.role === 'admin' || user.is_superuser : false
}

// ============================================================================
// ACCOUNT STATUS CHECKS
// ============================================================================

/**
 * Check if user is verified
 */
export const isVerified = (user: User | null): boolean => {
  return user ? user.is_verified : false
}

/**
 * Check if user account is approved
 */
export const isAccountApproved = (user: User | null): boolean => {
  return user ? user.is_account_approved : false
}

/**
 * Check if user is ready for production
 */
export const isReadyForProduction = (user: User | null): boolean => {
  return user
    ? user.is_verified && user.is_account_approved && !!user.company_name && !!user.phone_number
    : false
}

/**
 * Check if user can access production environment (requires KYC)
 */
export const canAccessProduction = (user: User | null): boolean => {
  return user
    ? user.is_verified && user.is_account_approved && user.is_kyc_verified && !!user.company_name && !!user.phone_number
    : false
}

// ============================================================================
// DATA FILTERING HELPERS
// ============================================================================

/**
 * Get roles that the current user can see/manage
 */
export const getVisibleRoles = (user: User | null): string[] => {
  if (!user) return []
  
  const allRoles = ['admin', 'merchant_admin', 'merchant_user', 'submerchant_admin', 'submerchant_user', 'auditor']
  
  if (user.role === 'admin' || user.is_superuser) {
    return allRoles
  }
  
  if (user.role === 'merchant_admin') {
    return ['merchant_admin', 'merchant_user']
  }
  
  if (user.role === 'submerchant_admin') {
    return ['submerchant_admin', 'submerchant_user']
  }
  
  return []
}

/**
 * Check if user can see a specific role in dropdowns/filters
 */
export const canSeeRole = (user: User | null, role: string): boolean => {
  return getVisibleRoles(user).includes(role)
}

/**
 * Get merchant IDs that the current user can access
 */
export const getAccessibleMerchantIds = (user: User | null): number[] => {
  if (!user) return []
  
  if (user.role === 'admin' || user.is_superuser) {
    return [] // Can access all merchants
  }
  
  if (user.merchant) {
    return [user.merchant]
  }
  
  return []
}

/**
 * Check if user can access a specific merchant's data
 */
export const canAccessMerchant = (user: User | null, merchantId: number): boolean => {
  if (!user) return false
  
  if (user.role === 'admin' || user.is_superuser) {
    return true // Can access all merchants
  }
  
  return user.merchant === merchantId
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get user's full name
 */
export const getUserFullName = (user: User | null): string => {
  return user ? `${user.first_name} ${user.last_name}` : ''
}

/**
 * Get user's display name (company name or full name)
 */
export const getUserDisplayName = (user: User | null): string => {
  if (!user) return ''
  return user.company_name || getUserFullName(user)
}

/**
 * Get user's role display name
 */
export const getUserRoleDisplay = (user: User | null): string => {
  if (!user) return ''

  const roleMap: Record<string, string> = {
    admin: 'Platform Admin',
    merchant_admin: 'Merchant Admin',
    merchant_user: 'Merchant User',
    submerchant_admin: 'Submerchant Admin',
    submerchant_user: 'Submerchant User',
    auditor: 'Auditor',
  }

  return roleMap[user.role] || user.role
}

/**
 * Get user's environment
 */
export const getUserEnvironment = (user: User | null): 'production' | 'sandbox' => {
  return user ? user.environment : 'sandbox'
}

/**
 * Check if user is in production environment
 */
export const isInProduction = (user: User | null): boolean => {
  return getUserEnvironment(user) === 'production'
}

/**
 * Check if user is in sandbox environment
 */
export const isInSandbox = (user: User | null): boolean => {
  return getUserEnvironment(user) === 'sandbox'
}

/**
 * Format user creation date
 */
export const formatUserCreatedDate = (user: User | null): string => {
  if (!user?.created_at) return ''

  const date = new Date(user.created_at)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Format user last login date
 */
export const formatUserLastLogin = (user: User | null): string => {
  if (!user?.last_login) return 'Never'

  const date = new Date(user.last_login)
  const now = new Date()
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))

  if (diffInHours < 1) return 'Just now'
  if (diffInHours < 24) return `${diffInHours} hours ago`

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
